/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

// Session context collection module.
// Computes a device context once at app startup and caches the result.
// The context is sent as X-Client-Context on every API request.
// The server interprets the signals; the client is a dumb collector.

let _cachedHeader: string | null = null;
let _cachedContext: ReadonlyArray<unknown> | null = null;
let _initPromise: Promise<void> | null = null;

// ── Encoding constants ──────────────────────────────────────────

const SCHEMA_VERSION = 1;

// XOR key derived from version — rotates when schema changes.
// Not cryptographic, just anti-casual-inspection friction.
function _dk(v: number): Uint8Array {
	const s = v * 7919 + 104729;
	const k = new Uint8Array(16);
	for (let i = 0; i < 16; i++) k[i] = ((s * (i + 1) * 251) >>> 0) & 0xff;
	return k;
}

// Decoy positions for v1 schema — random-looking values inserted at these indices
// to make positional mapping harder. Server strips them.
const _DP = [3, 11, 19, 27, 35] as const;

// CRC16-CCITT for integrity verification
function _c16(data: ReadonlyArray<unknown>): number {
	const str = JSON.stringify(data);
	let crc = 0xffff;
	for (let i = 0; i < str.length; i++) {
		crc ^= str.charCodeAt(i) << 8;
		for (let j = 0; j < 8; j++) {
			crc = crc & 0x8000 ? (crc << 1) ^ 0x1021 : crc << 1;
			crc &= 0xffff;
		}
	}
	return crc;
}

function _h(str: string): string {
	let hash = 5381;
	for (let i = 0; i < str.length; i++) {
		hash = ((hash << 5) + hash + str.charCodeAt(i)) | 0;
	}
	return (hash >>> 0).toString(16);
}

// ── Signal collection ───────────────────────────────────────────

async function _gc(): Promise<ReadonlyArray<unknown>> {
	const v: Array<unknown> = [];
	const n = navigator as unknown as Record<string, unknown>;
	try {
		// [0] timestamp
		v.push(Date.now());

		// [1-7] screen & display
		v.push(window.screen?.width ?? 0);
		v.push(window.screen?.height ?? 0);
		v.push(window.screen?.colorDepth ?? 0);
		v.push(window.innerWidth ?? 0);
		v.push(window.innerHeight ?? 0);
		v.push(window.outerWidth ?? 0);
		v.push(window.outerHeight ?? 0);

		// [8] device pixel ratio
		v.push(window.devicePixelRatio ?? 1);

		// [9-14] navigator properties
		v.push(Intl?.DateTimeFormat?.()?.resolvedOptions?.()?.timeZone ?? null);
		v.push(navigator?.languages ? [...navigator.languages] : []);
		v.push(navigator?.hardwareConcurrency ?? null);
		v.push(n?.deviceMemory ?? null);
		v.push(navigator?.platform ?? null);
		v.push(navigator?.maxTouchPoints ?? 0);

		// [15-17] browser state
		v.push(navigator?.cookieEnabled ?? null);
		v.push(n?.webdriver ?? false);
		v.push(navigator?.plugins?.length ?? 0);

		// [18] chrome object presence
		v.push(typeof (window as unknown as Record<string, unknown>).chrome !== 'undefined');

		// [19-20] automation artifacts
		try {
			const w = window as unknown as Record<string, unknown>;
			const d = document as unknown as Record<string, unknown>;
			v.push(
				Object.getOwnPropertyNames(w).some((k) => k.startsWith('cdc_')) ||
				Object.getOwnPropertyNames(d).some((k) => k.startsWith('$cdc_')),
			);
		} catch { v.push(false); }

		try {
			const w = window as unknown as Record<string, unknown>;
			v.push(
				w.__nightmare !== undefined ||
				w._phantom !== undefined ||
				w.callPhantom !== undefined ||
				w.__selenium_unwrapped !== undefined ||
				w.__webdriver_evaluate !== undefined ||
				w.__driver_evaluate !== undefined ||
				document.querySelector('[selenium-evaluate]') !== null,
			);
		} catch { v.push(false); }

		// [21] performance.now precision
		try {
			const t1 = performance.now();
			const t2 = performance.now();
			v.push(t2 - t1 < 0.005 ? 2 : t1 % 1 === 0 ? 0 : 1);
		} catch { v.push(null); }

		// [22] canvas fingerprint
		try {
			const c = document.createElement('canvas');
			c.width = 280; c.height = 60;
			const x = c.getContext('2d');
			if (x) {
				x.textBaseline = 'alphabetic';
				x.fillStyle = '#f60';
				x.fillRect(125, 1, 62, 20);
				x.fillStyle = '#069';
				x.font = '11pt no-real-font-123';
				x.fillText('Cwm fjordbank glyph', 2, 15);
				x.fillStyle = 'rgba(102, 204, 0, 0.7)';
				x.font = '18pt Arial';
				x.fillText('Cwm fjordbank glyph', 4, 45);
				x.globalCompositeOperation = 'multiply';
				x.fillStyle = 'rgb(255,0,255)';
				x.beginPath();
				x.arc(50, 50, 50, 0, Math.PI * 2, true);
				x.closePath();
				x.fill();
				v.push(_h(c.toDataURL()));
			} else { v.push(null); }
		} catch { v.push(null); }

		// [23-27] WebGL
		try {
			const gl = document.createElement('canvas').getContext('webgl');
			if (gl) {
				const dbg = gl.getExtension('WEBGL_debug_renderer_info');
				v.push(dbg ? gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL) : null);
				v.push(dbg ? gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) : null);
				v.push(gl.getParameter(gl.VERSION));
				v.push(gl.getParameter(gl.SHADING_LANGUAGE_VERSION));
				v.push(gl.getParameter(gl.MAX_TEXTURE_SIZE));
			} else { v.push(null, null, null, null, null); }
		} catch { v.push(null, null, null, null, null); }

		// [28] audio fingerprint
		try {
			const ac = new OfflineAudioContext(1, 44100, 44100);
			const osc = ac.createOscillator();
			osc.type = 'triangle';
			osc.frequency.setValueAtTime(10000, ac.currentTime);
			const comp = ac.createDynamicsCompressor();
			comp.threshold.setValueAtTime(-50, ac.currentTime);
			comp.knee.setValueAtTime(40, ac.currentTime);
			comp.ratio.setValueAtTime(12, ac.currentTime);
			comp.attack.setValueAtTime(0, ac.currentTime);
			comp.release.setValueAtTime(0.25, ac.currentTime);
			osc.connect(comp);
			comp.connect(ac.destination);
			osc.start(0);
			const buf = await Promise.race([
				ac.startRendering(),
				new Promise<null>((r) => setTimeout(() => r(null), 200)),
			]);
			if (buf) {
				const data = buf.getChannelData(0);
				let sum = 0;
				for (let i = 4500; i < 5000; i++) sum += Math.abs(data[i]!);
				v.push(_h(sum.toString()));
			} else { v.push(null); }
		} catch { v.push(null); }

		// [29] font detection hash (instead of full array)
		try {
			const bf = ['monospace', 'sans-serif', 'serif'] as const;
			const tf = [
				'Arial', 'Courier New', 'Georgia', 'Times New Roman', 'Verdana',
				'Trebuchet MS', 'Palatino', 'Impact', 'Comic Sans MS', 'Lucida Console',
				'Segoe UI', 'Roboto', 'Helvetica Neue', 'SF Pro',
			];
			const sp = document.createElement('span');
			sp.style.position = 'absolute';
			sp.style.left = '-9999px';
			sp.style.fontSize = '72px';
			sp.textContent = 'mmmmmmmmmmlli';
			document.body.appendChild(sp);
			const bw: Record<string, number> = {};
			for (const b of bf) { sp.style.fontFamily = b; bw[b] = sp.offsetWidth; }
			const det: Array<string> = [];
			for (const f of tf) {
				for (const b of bf) {
					sp.style.fontFamily = `"${f}", ${b}`;
					if (sp.offsetWidth !== bw[b]) { det.push(f); break; }
				}
			}
			document.body.removeChild(sp);
			v.push(_h(det.sort().join(',')));
		} catch { v.push(null); }

		// [30] math engine fingerprint
		try {
			v.push(_h([
				Math.tan(-1e300), Math.atan2(1, 2), Math.sin(1),
				Math.pow(Math.PI, -100), Math.exp(100), Math.log(2.0001),
				Math.sqrt(2), Math.E * Math.PI,
			].join(',')));
		} catch { v.push(null); }

		// [31] error stack signature
		try {
			const err = new Error('_fp');
			v.push(_h((err.stack ?? '').split('\n').slice(0, 3).join('')));
		} catch { v.push(null); }

		// [32] storage quota
		try {
			if (navigator.storage?.estimate) {
				const est = await navigator.storage.estimate();
				v.push(est.quota ?? null);
			} else { v.push(null); }
		} catch { v.push(null); }

		// [33] screen available delta (reveals taskbar/dock size)
		try {
			v.push((window.screen?.availWidth ?? 0) - (window.screen?.width ?? 0));
		} catch { v.push(null); }

		// [34] media devices count
		try {
			const devices = await navigator.mediaDevices?.enumerateDevices();
			if (devices) {
				v.push([
					devices.filter(d => d.kind === 'videoinput').length,
					devices.filter(d => d.kind === 'audioinput').length,
					devices.filter(d => d.kind === 'audiooutput').length,
				]);
			} else { v.push(null); }
		} catch { v.push(null); }

		// [35] Intl API fingerprint
		try {
			const nf = new Intl.NumberFormat().resolvedOptions();
			const df = new Intl.DateTimeFormat().resolvedOptions();
			v.push(_h(`${nf.locale}|${nf.numberingSystem}|${df.calendar}|${df.timeZone}`));
		} catch { v.push(null); }

		// [36] WebGL extended parameters hash
		try {
			const gl = document.createElement('canvas').getContext('webgl');
			if (gl) {
				const params = [
					gl.getParameter(gl.MAX_VERTEX_ATTRIBS),
					gl.getParameter(gl.MAX_VARYING_VECTORS),
					gl.getParameter(gl.MAX_VERTEX_UNIFORM_VECTORS),
					gl.getParameter(gl.MAX_FRAGMENT_UNIFORM_VECTORS),
					gl.getParameter(gl.MAX_RENDERBUFFER_SIZE),
					gl.getParameter(gl.MAX_VIEWPORT_DIMS),
					gl.getParameter(gl.MAX_VERTEX_TEXTURE_IMAGE_UNITS),
					gl.getParameter(gl.MAX_TEXTURE_IMAGE_UNITS),
					gl.getParameter(gl.MAX_COMBINED_TEXTURE_IMAGE_UNITS),
					gl.getParameter(gl.ALIASED_LINE_WIDTH_RANGE),
					gl.getParameter(gl.ALIASED_POINT_SIZE_RANGE),
				];
				v.push(_h(JSON.stringify(params)));
			} else { v.push(null); }
		} catch { v.push(null); }

		// [37] Canvas text metrics hash
		try {
			const c = document.createElement('canvas');
			c.width = 200; c.height = 50;
			const x = c.getContext('2d');
			if (x) {
				x.font = '14px Arial';
				const m = x.measureText('The quick brown fox jumps over the lazy dog');
				v.push(_h(`${m.width}|${m.actualBoundingBoxAscent}|${m.actualBoundingBoxDescent}`));
			} else { v.push(null); }
		} catch { v.push(null); }

		// [38] WebGPU availability + features
		try {
			const gpu = (navigator as unknown as Record<string, unknown>).gpu as {requestAdapter?: () => Promise<unknown>} | undefined;
			if (gpu?.requestAdapter) {
				const adapter = await Promise.race([
					gpu.requestAdapter(),
					new Promise<null>((r) => setTimeout(() => r(null), 500)),
				]) as {features?: Set<string>; info?: Record<string, string>} | null;
				if (adapter?.features) {
					v.push(_h([...adapter.features].sort().join(',') + '|' + JSON.stringify(adapter.info ?? {})));
				} else { v.push(null); }
			} else { v.push(null); }
		} catch { v.push(null); }

		// [39] font set size
		try {
			v.push(document.fonts?.size ?? null);
		} catch { v.push(null); }

		// [40] UA Client Hints
		try {
			const uaData = n?.userAgentData as Record<string, unknown> | undefined;
			if (uaData) {
				v.push([
					(uaData.brands as Array<{brand: string; version: string}> | undefined)
						?.map((b) => `${b.brand}/${b.version}`)?.join(',') ?? null,
					uaData.mobile ?? null,
					uaData.platform ?? null,
				]);
			} else { v.push(null); }
		} catch { v.push(null); }

	} catch {
		// Best-effort collection
	}
	return v;
}

// ── Encoding pipeline ───────────────────────────────────────────

function _enc(data: ReadonlyArray<unknown>): string {
	// Insert decoy values at predetermined positions
	const withDecoys = [...data];
	for (const pos of _DP) {
		if (pos <= withDecoys.length) {
			withDecoys.splice(pos, 0, Math.random() * 65536 | 0);
		}
	}

	// Append CRC16 of real data (without decoys)
	withDecoys.push(_c16(data));

	const json = JSON.stringify(withDecoys);
	const key = _dk(SCHEMA_VERSION);

	// XOR encode
	const bytes = new Uint8Array(json.length + 1);
	bytes[0] = SCHEMA_VERSION;
	for (let i = 0; i < json.length; i++) {
		bytes[i + 1] = json.charCodeAt(i) ^ key[i % key.length]!;
	}

	// Base64 encode
	let binary = '';
	for (let i = 0; i < bytes.length; i++) {
		binary += String.fromCharCode(bytes[i]!);
	}
	return btoa(binary);
}

/** Returns the cached encoded context header value, or null if not yet computed. */
export function getClientContextHeader(): string | null {
	return _cachedHeader;
}

/** Returns the cached raw context array, or null if not yet computed. */
export function getClientContext(): ReadonlyArray<unknown> | null {
	return _cachedContext;
}

/** Compute the context eagerly. Call once at app startup. Safe to call multiple times. */
export function initializeClientFingerprint(): Promise<void> {
	if (!_initPromise) {
		_initPromise = _gc().then((ctx) => {
			_cachedContext = ctx;
			try {
				_cachedHeader = _enc(ctx);
			} catch {
				_cachedHeader = null;
			}
		}).catch(() => {
			// Non-fatal — missing context is itself a signal to the server.
		});
	}
	return _initPromise;
}
